/**
 * Whether a subscription status string indicates expired (not grace).
 * @param {string|number|null|undefined} status
 * @returns {boolean}
 */
export function isSubscriptionExpiredStatus(status) {
  const s = (status ?? '').toString().trim().toLowerCase();
  if (!s) return false;
  return s === 'expired' || s.includes('expired');
}

/**
 * Expired from displayed `status` or raw creditSummary `currentPlan.status`.
 * Handles string "Expired" and numeric code `1` (see useSubscriptionHistory transformStatus).
 * @param {{ status?: string, rawData?: { status?: string|number } }|null|undefined} subscriptionLike
 */
export function isSubscriptionExpiredInSummary(subscriptionLike) {
  if (!subscriptionLike) return false;
  if (isSubscriptionExpiredStatus(subscriptionLike.status)) return true;
  const raw = subscriptionLike.rawData?.status;
  if (raw == null || raw === '') return false;
  if (raw === 1 || raw === '1') return true;
  if (typeof raw === 'string') return isSubscriptionExpiredStatus(raw);
  return false;
}

/**
 * Whether a subscription status string indicates grace.
 * - Exact / compact API tokens (`grace period`, `graceperiod`)
 * - Phrases containing those tokens
 * - Same tolerance as CurrentPlanCard: substring `grace` catches "In Grace", "Monthly in grace", etc.
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function isSubscriptionGraceStatus(status) {
  const s = (status ?? '').trim().toLowerCase();
  if (!s) return false;
  if (s === 'grace period' || s === 'graceperiod') return true;
  if (s.includes('grace period') || s.includes('graceperiod')) return true;
  if (s.includes('grace')) return true;
  return false;
}

/**
 * Grace from displayed `status` or raw creditSummary `currentPlan.status` when the UI string was normalized.
 * @param {{ status?: string, rawData?: { status?: string|number } }|null|undefined} subscriptionLike
 */
export function isSubscriptionInGraceState(subscriptionLike) {
  if (!subscriptionLike) return false;
  if (isSubscriptionGraceStatus(subscriptionLike.status)) return true;
  const raw = subscriptionLike.rawData?.status;
  if (typeof raw === 'string') return isSubscriptionGraceStatus(raw);
  return false;
}

/**
 * creditSummary `currentPlan.paymentFailureAt`: non-zero UTC ms timestamp means grace is due to payment failure.
 * @param {number|string|null|undefined} paymentFailureAt
 * @returns {boolean}
 */
export function isPaymentFailureGrace(paymentFailureAt) {
  if (paymentFailureAt == null || paymentFailureAt === '') return false;
  const n = Number(paymentFailureAt);
  if (Number.isNaN(n)) return false;
  return n !== 0;
}

/**
 * Shared 14-day grace banner variant for legacy MUB/monthly and PAYG.
 * `paymentFailureAt` non-zero → payment-failure copy; `0`/absent → standard grace copy.
 *
 * @param {number|string|null|undefined} paymentFailureAt - creditSummary `currentPlan.paymentFailureAt`
 * @param {{ payg?: boolean }} [options]
 * @returns {'paygGracePaymentFailure'|'paygGrace'|'monthlyGracePaymentFailure'|'monthlyGraceChoosePlan'}
 */
export function resolveMonthlyStyleGraceBannerType(paymentFailureAt, { payg = false } = {}) {
  if (isPaymentFailureGrace(paymentFailureAt)) {
    return payg ? 'paygGracePaymentFailure' : 'monthlyGracePaymentFailure';
  }
  return payg ? 'paygGrace' : 'monthlyGraceChoosePlan';
}
